import {
  Amount,
  GlobalActions,
  ProductConcentration,
  RealEconomyProductCategory,
} from '../../../../model';
import {
  myWalletSliceActions,
  retrievePortfolioCompositionMacroCategory,
  retrieveRealEconomyAssetSummary,
  retrieveRealEconomyProductsConcentration,
  retrieveRealEconomyResidueDuration,
  retrieveRealEconomyRiskProfile,
  selectRealEconomyAssetSummary,
  selectRealEconomyProductsConcentration,
} from '../../../../store';
import { useCallback, useEffect, useMemo } from 'react';
import { useMyWalletLogic } from '../../my-wallet/my-wallet-logic';
import { useWalletLogic } from '../../wallet-logic';
import { useAppSelector } from '../../../redux';
import { useDispatch } from 'react-redux';
import {
  PortfolioSummaryByMacroCategory,
  ResidueDuration,
  RealEconomyAssetSummary,
  RiskProfile,
  RealEconomyProductsConcentration,
} from '../../../../model/wallet/wallet-ow';
import { env } from '../../../../env';

export interface ErDetailsPageLogic {
  totalAmount: Amount;
  products: ProductConcentration[];
  isLoadingWallet: boolean;
  isLoadingPortfolioSummaryChart: boolean;
  portfolioSummary: PortfolioSummaryByMacroCategory;
  realEconomyAssetSummary: RealEconomyAssetSummary;
  isLoadingRealEconomyAssetSummary: boolean;
  isLoadingRealEconomyResidueDuration: boolean;
  realEconomyProductsConcentration: RealEconomyProductsConcentration;
  isLoadingRealEconomyProductsConcentration: boolean;
  realEconomyResidueDuration: ResidueDuration;
  isLoadingRealEconomyRiskProfile: boolean;
  realEconomyRiskProfile: RiskProfile;
}

export const useErDetailLogic = (
  globalActions: GlobalActions
): ErDetailsPageLogic => {
  useWalletLogic();
  const { wallet } = useMyWalletLogic(globalActions);
  const {
    portfolioSummary,
    realEconomyResidueDuration,
    realEconomyRiskProfile,
  } = useAppSelector((state) => state.features.walletStore.myWallet);

  const realEconomyAssetSummary = useAppSelector(selectRealEconomyAssetSummary);
  const realEconomyProductsConcentration = useAppSelector(
    selectRealEconomyProductsConcentration
  );

  const dispatch = useDispatch();

  const isLoadingRealEconomyAssetSummary = useMemo(() => {
    return realEconomyAssetSummary === null;
  }, [realEconomyAssetSummary]);

  const isLoadingRealEconomyResidueDuration = useMemo(() => {
    return realEconomyResidueDuration === null;
  }, [realEconomyResidueDuration]);

  const isLoadingRealEconomyRiskProfile = useMemo(() => {
    return realEconomyRiskProfile === null;
  }, [realEconomyRiskProfile]);

  const isLoadingRealEconomyProductsConcentration = useMemo(() => {
    return realEconomyProductsConcentration === null;
  }, [realEconomyProductsConcentration]);

  const isLoadingWallet = useMemo(() => {
    return wallet === null;
  }, [wallet]);

  const isLoadingPortfolioSummaryChart = useMemo(() => {
    return portfolioSummary === null;
  }, [portfolioSummary]);
  const totalAmount: Amount = useMemo(() => {
    const realEconomy = wallet?.macroCategories?.find(
      (m) => m.type === 'RealEconomy'
    );
    return realEconomy?.amount;
  }, [wallet]);

  const getPaperContractCode = useCallback((v: string) => {
    const gruppi = v?.split('-');
    if (gruppi?.length > 3) {
      return gruppi.slice(3).join('-');
    }
    return v;
  }, []);

  const products: ProductConcentration[] = useMemo(() => {
    const realEconomy = wallet?.macroCategories?.find(
      (m) => m.type === 'RealEconomy'
    );
    if (realEconomy) {
      const total = realEconomy.amount?.value;
      const realEconomyProducts = (
        realEconomy?.categories as RealEconomyProductCategory[]
      ).flatMap((u) => u.macroProducts.flatMap((mp) => mp.products));

      return realEconomyProducts.map((rep) => {
        const years = 0;
        const actualYears = 0;
        return {
          product: { ...rep, contract: getPaperContractCode(rep.contract) },
          quote: +parseFloat(
            '' + (total ? (100 * (rep.counterValue?.value || 0)) / total : 0)
          ).toFixed(2),
          years,
          expirationPercentage: years > 0 ? (actualYears * 100) / years : 100,
        } as ProductConcentration;
      });
    }
    return [];
  }, [wallet]);

  useEffect(() => {
    dispatch(retrievePortfolioCompositionMacroCategory());
    dispatch(retrieveRealEconomyResidueDuration());
    dispatch(retrieveRealEconomyRiskProfile());
    dispatch(retrieveRealEconomyAssetSummary());
    dispatch(retrieveRealEconomyProductsConcentration());
  }, []);

  useEffect(() => {
    return () => {
      if (env.appModule === 'web') {
        dispatch(myWalletSliceActions.resetWalletState());
      }
    };
  }, [dispatch]);

  return {
    totalAmount,
    products,
    isLoadingWallet,
    portfolioSummary,
    realEconomyAssetSummary,
    isLoadingRealEconomyAssetSummary,
    realEconomyProductsConcentration,
    isLoadingRealEconomyProductsConcentration,
    isLoadingPortfolioSummaryChart,
    realEconomyResidueDuration,
    isLoadingRealEconomyResidueDuration,
    isLoadingRealEconomyRiskProfile,
    realEconomyRiskProfile,
  };
};
